import type React from "react";

// The single owned accessibility policy for legacy Redux Form fields.
//
// This is a behaviour-preserving move of `fieldAccessibility` and `FieldMessages`
// out of inputs.jsx. Every existing consumer imports from here, so there is one
// implementation of the help/error wiring rather than one per extracted file as
// fields leave inputs.jsx over the remaining waves.
//
// It deliberately does NOT compose the owned primitives' Field parts. Adopting
// them would change the rendered markup of every field at once, inside a wave
// whose subject is a different dependency; that is a separate, visually gated
// change. See the "pure-move reading" in docs/ui-boundary-migration-plan.md.
//
// This module owns legacy Redux Form translation on purpose. Primitives must
// never see `field.input` or `field.meta`; that boundary is enforced by
// components/primitives/importBoundary.test.ts.

/** The Redux Form field object these legacy fields are called with. */
export type LegacyField = {
  "aria-describedby"?: string;
  /**
   * Suppresses the error message without suppressing the caller's own
   * validation-state wrapper class. Used by `Hidden`, whose error would
   * otherwise render next to an invisible input.
   */
  hideErrorMessage?: boolean;
  helpBlock?: React.ReactNode;
  meta?: { error?: React.ReactNode; touched?: boolean };
  required?: boolean;
};

export type FieldAccessibility = {
  errorId: string | undefined;
  hasError: boolean;
  helpId: string | undefined;
  inputProps: {
    "aria-describedby": string | undefined;
    "aria-invalid": true | undefined;
    "aria-required": true | undefined;
  };
};

export function fieldAccessibility(field: LegacyField, inputId: string): FieldAccessibility {
  // `hideErrorMessage` folds into `hasError` rather than only suppressing the
  // rendered element, so a suppressed error can never leave `aria-invalid` set
  // or `aria-describedby` pointing at an id that was never emitted.
  const hasError = Boolean(field.meta?.touched && field.meta?.error && !field.hideErrorMessage);
  const helpId = field.helpBlock ? `${inputId}-help` : undefined;
  const errorId = hasError ? `${inputId}-error` : undefined;
  const describedBy = [field["aria-describedby"], helpId, errorId].filter(Boolean).join(" ") || undefined;

  return {
    errorId,
    hasError,
    helpId,
    inputProps: {
      "aria-describedby": describedBy,
      "aria-invalid": hasError || undefined,
      "aria-required": field.required || undefined,
    },
  };
}

export function FieldMessages({
  accessibility,
  field,
}: {
  accessibility: FieldAccessibility;
  field: LegacyField;
}): React.ReactElement {
  return (
    <>
      {field.helpBlock && (
        <p id={accessibility.helpId} className="app-form-help">
          {field.helpBlock}
        </p>
      )}
      {accessibility.hasError && (
        <div id={accessibility.errorId} className="app-form-help with-errors" role="alert">
          {field.meta?.error}
        </div>
      )}
    </>
  );
}
